// SPDX-License-Identifier: GPL-3.0-or-later
// The run director. It owns the state of a run: the stage, the goal, the perk draft and
// the timed events. The table rules call it through RunRulesPort. The page calls it through
// RunClientPort. The rules of the run are in the run design document.
//
// Pure: no clock, no random source of its own, no I/O. Time comes in as game ms (dt).
// The draft random source is injected. This file imports no file of src/engine.
// A tick, a hold tick, scaleFor and addStagePoints allocate nothing. An edge event (stage, draft,
// pick, hurry-up, frenzy start, chain award) sends one small event object. A timed event that
// ends sends a shared constant event.

import type { RulesContext } from "../../shared/contracts.ts";
import { clamp } from "../animation.ts";
import type { ShotId } from "../callouts.ts";
import { TIMES } from "../rules/constants.ts";
import { type BossFeedbackEvent, BossFight } from "./boss.ts";
import { AUTO_PICK_MS, drawDraft } from "./draft.ts";
import { emptyStacks, foldPerks, PERKS, RUN_LIMITS } from "./perks.ts";
import { goalMet, STAGE_COUNT, STAGES, type StageTwistValues, twistFor } from "./stages.ts";
import { ComboChain, Frenzy, HurryUp, newTimedState } from "./timed-events.ts";
import type {
  BossHitSource,
  BossState,
  DraftIndex,
  PerkId,
  RunClientPort,
  RunPayer,
  RunRulesPort,
  RunSignalKind,
  RunSignals,
  RunState,
  RunView,
  ScoreChannel,
  StageDef,
  TimedEventKind,
} from "./types.ts";

/** Points of one lit shot. The award uses the channel "event", so no perk scales it. */
export const LIT_SHOT_POINTS = 10_000;

/** The shot that collects a lit extra ball. */
export const EXTRA_BALL_SHOT: ShotId = "orbitRight";

/** The frenzy doubles the switch channel. */
const FRENZY_SWITCH_SCALE = 2;

// ───────────────────────────── feedback events ─────────────────────────────

/**
 * The feedback events that the director sends. The shape of each event is the shape that the
 * integration slice adds to `src/shared/feedback/events.ts`. A stage number is 1-based.
 */
export type RunFeedbackEvent =
  | { readonly kind: "stage_start"; readonly stage: number; readonly label: string }
  | { readonly kind: "stage_clear"; readonly stage: number; readonly scoreDelta: number }
  | {
      readonly kind: "draft_open";
      readonly choices: readonly [PerkId | null, PerkId | null, PerkId | null];
    }
  | { readonly kind: "perk_pick"; readonly perkId: PerkId; readonly label: string }
  | { readonly kind: "run_clear" }
  | { readonly kind: "hurry_up_start"; readonly seconds: number; readonly scoreDelta: number }
  | { readonly kind: "hurry_up_collect"; readonly scoreDelta: number }
  | { readonly kind: "frenzy_start"; readonly seconds: number }
  | { readonly kind: "combo_chain"; readonly count: number; readonly scoreDelta: number }
  | {
      readonly kind: "timed_event_end";
      readonly event: TimedEventKind;
      readonly collected: boolean;
    }
  | { readonly kind: "extra_ball_lit" }
  | { readonly kind: "extra_ball" }
  | BossFeedbackEvent;

/** Where the director sends its events. The feedback bus fits this shape. */
interface RunFeedbackSink {
  emit(ev: RunFeedbackEvent): void;
}

const NO_SINK: RunFeedbackSink = {
  emit(): void {
    // Nobody listens.
  },
};

/** Shared events with no variable field. They allocate nothing. */
const RUN_CLEAR_EVENT: RunFeedbackEvent = { kind: "run_clear" };
const EXTRA_BALL_LIT_EVENT: RunFeedbackEvent = { kind: "extra_ball_lit" };
const EXTRA_BALL_EVENT: RunFeedbackEvent = { kind: "extra_ball" };
const HURRY_END_EVENT: RunFeedbackEvent = {
  kind: "timed_event_end",
  event: "hurry_up",
  collected: false,
};
const FRENZY_END_EVENT: RunFeedbackEvent = {
  kind: "timed_event_end",
  event: "frenzy",
  collected: true,
};

// ───────────────────────────── options and state ─────────────────────────────

export interface RunDirectorOptions {
  /** The random source of the draft. Make it from the game seed. */
  readonly draftRng: { next(): number };
  /** Where the events go. Unset: nowhere. */
  readonly feedback?: RunFeedbackSink;
  /** The first stage of the next game, 1-based. Unset: 1. A featured turn always starts at 1. */
  readonly startStage?: number;
  /** True when a featured turn plays. Unset: false. */
  readonly featured?: boolean;
}

/** The director: what the rules call, and what the page calls. */
export type RunDirector = RunRulesPort & RunClientPort;

/** The seconds of a timer, rounded up. */
function secondsOf(ms: number): number {
  return Math.ceil(ms / 1_000);
}

/** The state of a new run. `boss` is the state object of the boss fight of the run. */
function newState(featured: boolean, boss: BossState): RunState {
  const stacks = emptyStacks();
  return {
    phase: "idle",
    stageIndex: 0,
    startIndex: 0,
    featured,
    signals: {
      stagePoints: 0,
      modesFinished: 0,
      multiballs: 0,
      jackpots: 0,
      litShots: 0,
      combos: 0,
      banks: 0,
      bumperHits: 0,
    },
    stacks,
    effects: foldPerks(stacks),
    draft: { choices: [null, null, null], count: 0, elapsedMs: 0, autoPick: false },
    events: {
      hurry_up: newTimedState("hurry_up"),
      frenzy: newTimedState("frenzy"),
      combo_chain: newTimedState("combo_chain"),
    },
    extraBall: { lit: false, litTotal: 0, collected: 0, lastLitStage: 0 },
    boss,
    stagePoints: new Array<number>(STAGE_COUNT).fill(0),
    cleared: 0,
  };
}

/** Set every count of the stage to 0. */
function zeroSignals(s: RunSignals): void {
  s.stagePoints = 0;
  s.modesFinished = 0;
  s.multiballs = 0;
  s.jackpots = 0;
  s.litShots = 0;
  s.combos = 0;
  s.banks = 0;
  s.bumperHits = 0;
}

// ───────────────────────────── the director ─────────────────────────────

class Director implements RunDirector {
  private readonly state: RunState;
  private readonly hurry: HurryUp;
  private readonly frenzy: Frenzy;
  private readonly chain: ComboChain;
  private readonly fight: BossFight;
  private readonly rng: { next(): number };
  private readonly sink: RunFeedbackSink;
  private payer: RunPayer | null = null;
  /** Balls that picked perks add to play. The rules take them at the next tick. */
  private bonusBalls = 0;
  /** The first stage of the next game, 1-based. */
  private startStage = 1;
  /** The stage that runs now. The twist of it is read once at the stage start. */
  private stage: StageDef = STAGES[0] as StageDef;
  private twist: StageTwistValues = twistFor(STAGES[0] as StageDef);

  constructor(options: RunDirectorOptions) {
    this.sink = options.feedback ?? NO_SINK;
    this.fight = new BossFight(this.sink);
    this.state = newState(options.featured === true, this.fight.state);
    this.hurry = new HurryUp(this.state.events.hurry_up);
    this.frenzy = new Frenzy(this.state.events.frenzy);
    this.chain = new ComboChain(this.state.events.combo_chain);
    this.rng = options.draftRng;
    if (options.startStage !== undefined) this.setStartStage(options.startStage);
  }

  // ───── RunClientPort ─────

  setStartStage(stage: number): void {
    if (!Number.isFinite(stage)) return;
    this.startStage = clamp(Math.trunc(stage), 1, STAGE_COUNT);
  }

  setFeatured(on: boolean): void {
    this.state.featured = on;
    if (!on) this.state.draft.autoPick = false;
  }

  pick(index: DraftIndex): boolean {
    const s = this.state;
    if (s.phase !== "draft") return false;
    const id = s.draft.choices[index];
    if (id === null || id === undefined) return false;
    s.stacks[id] += 1;
    s.effects = foldPerks(s.stacks);
    const def = PERKS.find((p) => p.id === id);
    if (def?.effect.kind === "add_ball") this.bonusBalls += def.effect.balls;
    this.sink.emit({ kind: "perk_pick", perkId: id, label: def?.label ?? id });
    this.beginStage(s.stageIndex + 1);
    return true;
  }

  view(): RunView {
    return this.state;
  }

  // ───── RunRulesPort: the game and the payer ─────

  attachPayer(payer: RunPayer): void {
    this.payer = payer;
  }

  onGameStart(_ctx: RulesContext): void {
    const s = this.state;
    s.startIndex = s.featured ? 0 : this.startStage - 1;
    for (const def of PERKS) s.stacks[def.id] = 0;
    s.effects = foldPerks(s.stacks);
    s.cleared = 0;
    s.stagePoints.fill(0);
    this.bonusBalls = 0;
    const eb = s.extraBall;
    eb.lit = false;
    eb.litTotal = 0;
    eb.collected = 0;
    eb.lastLitStage = 0;
    this.endTimers();
    this.frenzy.reset();
    this.beginStage(s.startIndex);
  }

  onBallEnd(): void {
    this.endTimers();
    // The boss keeps its HP into the next ball. Its wind-up and its shield stop.
    this.fight.pause();
  }

  /** The three timers stop with no cue. The extra ball stays lit. */
  private endTimers(): void {
    this.hurry.end();
    this.frenzy.end();
    this.chain.end();
  }

  // ───── RunRulesPort: time ─────

  tick(ctx: RulesContext, dtMs: number): void {
    if (!this.isRunning()) return;
    // The boss waits while no ball is in play (between two balls).
    if (ctx.ballsInPlay > 0) this.fight.tick(dtMs);
    if (this.hurry.tick(dtMs)) this.sink.emit(HURRY_END_EVENT);
    if (this.frenzy.tick(dtMs)) this.sink.emit(FRENZY_END_EVENT);
    // The end of a combo window has no cue: it happens after every shot with no combo.
    this.chain.tick(dtMs);
  }

  holdActive(): boolean {
    return this.state.phase === "draft";
  }

  onHoldTick(dtMs: number): void {
    const s = this.state;
    if (s.phase !== "draft" || !(dtMs > 0) || !Number.isFinite(dtMs)) return;
    const d = s.draft;
    d.elapsedMs += dtMs;
    if (d.autoPick && d.elapsedMs >= AUTO_PICK_MS) this.pick(0);
  }

  // ───── RunRulesPort: signals and shots ─────

  signal(ctx: RulesContext, kind: RunSignalKind): void {
    if (!this.isRunning()) return;
    const sig = this.state.signals;
    switch (kind) {
      case "mode_finished":
        sig.modesFinished += 1;
        break;
      case "multiball":
        sig.multiballs += 1;
        break;
      case "jackpot":
        sig.jackpots += 1;
        break;
      case "combo":
        this.onCombo(ctx);
        return;
      case "bank":
        this.onBank();
        return;
      case "bumper":
        this.onBumper();
        return;
    }
    this.checkGoal();
  }

  onShot(ctx: RulesContext, shot: ShotId): void {
    if (!this.isRunning()) return;
    // The extra ball comes first: a stage clear in this call must not collect the ball it lights.
    this.collectExtraBall(shot);
    this.chain.shot(this.comboWindowMs(TIMES.comboWindowMs));
    this.collectHurry(ctx, shot);
    // The lit shot comes last: its award can meet the goal.
    this.payLitShot(ctx, shot);
  }

  addStagePoints(_ctx: RulesContext, points: number): void {
    const s = this.state;
    if (!(points > 0)) return;
    // A draft holds play. The stage is over, so a late award does not count.
    if (s.phase === "play") {
      this.countPoints(points);
      this.checkGoal();
    } else if (s.phase === "clear") {
      this.countPoints(points);
    }
  }

  private countPoints(points: number): void {
    const s = this.state;
    s.signals.stagePoints += points;
    s.stagePoints[s.stageIndex] = s.signals.stagePoints;
  }

  // ───── RunRulesPort: values the rules read ─────

  scaleFor(channel: ScoreChannel): number {
    if (channel === "combo" || channel === "other" || channel === "event") return 1;
    const s = this.state;
    let scale = s.effects.channelScale[channel];
    if (channel === "ramp" || channel === "orbit") scale *= this.twist.shotScale;
    else if (channel === "switch" && s.events.frenzy.active) scale *= FRENZY_SWITCH_SCALE;
    return clamp(scale, 1, RUN_LIMITS.MAX_CHANNEL_SCALE);
  }

  launchSaveMs(baseMs: number): number {
    if (this.state.phase === "idle") return baseMs;
    const ms = (this.twist.launchSaveMs ?? baseMs) + this.state.effects.launchSaveBonusMs;
    return clamp(ms, RUN_LIMITS.LAUNCH_SAVE_MIN_MS, RUN_LIMITS.LAUNCH_SAVE_MAX_MS);
  }

  comboWindowMs(baseMs: number): number {
    if (this.state.phase === "idle") return baseMs;
    const ms = (this.twist.comboMs ?? baseMs) + this.state.effects.comboWindowBonusMs;
    return clamp(ms, RUN_LIMITS.COMBO_WINDOW_MIN_MS, RUN_LIMITS.COMBO_WINDOW_MAX_MS);
  }

  modeMs(baseMs: number): number {
    if (this.state.phase === "idle") return baseMs;
    const ms = Math.round(baseMs * this.twist.modeScale) + this.state.effects.modeTimeBonusMs;
    const min = baseMs * RUN_LIMITS.MODE_TIME_MIN_SCALE;
    return clamp(ms, min, baseMs + RUN_LIMITS.MODE_TIME_MAX_BONUS_MS);
  }

  bonusMultStart(): number {
    return this.state.effects.bonusMultStart;
  }

  shotLamp(shot: ShotId): 0 | 1 | 2 {
    const s = this.state;
    if (s.phase === "idle") return 0;
    if (s.events.hurry_up.active && shot === this.stage.hurryShot) return 2;
    if (s.extraBall.lit && shot === EXTRA_BALL_SHOT) return 2;
    return this.stage.litShots.includes(shot) ? 1 : 0;
  }

  extraBallsEarned(): number {
    return Math.min(this.state.extraBall.collected, RUN_LIMITS.MAX_EXTRA_BALLS_GAME);
  }

  // ───── RunRulesPort: the boss and the balls of the perks ─────

  bossHit(ctx: RulesContext, source: BossHitSource): boolean {
    // The draft holds play and the last clear ends the stages, so only a play phase has a boss.
    if (this.state.phase !== "play") return false;
    if (this.fight.hit(source, ctx.timeMs) !== "defeated") return false;
    this.clearStage();
    return true;
  }

  takeBonusBalls(room: number): number {
    if (!Number.isFinite(room) || room <= 0) return 0;
    const n = Math.min(this.bonusBalls, Math.trunc(room));
    this.bonusBalls -= n;
    return n;
  }

  // ───── timed events ─────

  private onCombo(ctx: RulesContext): void {
    this.state.signals.combos += 1;
    const points = this.chain.combo();
    if (points <= 0) return;
    this.sink.emit({ kind: "combo_chain", count: this.chain.state.count, scoreDelta: points });
    this.pay(ctx, points, "Combo chain");
  }

  private onBank(): void {
    this.state.signals.banks += 1;
    if (!this.hurry.start(this.state.effects.hurryBonusMs)) return;
    const h = this.hurry.state;
    this.sink.emit({ kind: "hurry_up_start", seconds: secondsOf(h.totalMs), scoreDelta: h.value });
  }

  private onBumper(): void {
    this.state.signals.bumperHits += 1;
    if (!this.frenzy.hit(this.state.effects.frenzyBonusMs)) return;
    this.sink.emit({ kind: "frenzy_start", seconds: secondsOf(this.frenzy.state.totalMs) });
  }

  private collectHurry(ctx: RulesContext, shot: ShotId): void {
    if (shot !== this.stage.hurryShot) return;
    const points = this.hurry.collect();
    if (points <= 0) return;
    this.sink.emit({ kind: "hurry_up_collect", scoreDelta: points });
    this.pay(ctx, points, "Hurry-up");
  }

  private payLitShot(ctx: RulesContext, shot: ShotId): void {
    if (this.state.phase === "draft" || !this.stage.litShots.includes(shot)) return;
    this.state.signals.litShots += 1;
    this.pay(ctx, LIT_SHOT_POINTS, "Lit shot");
    this.checkGoal();
  }

  private pay(ctx: RulesContext, points: number, reason: string): void {
    this.payer?.pay(ctx, points, reason, "event");
  }

  // ───── extra ball ─────

  private collectExtraBall(shot: ShotId): void {
    const eb = this.state.extraBall;
    if (shot !== EXTRA_BALL_SHOT || !eb.lit) return;
    eb.lit = false;
    eb.collected = Math.min(eb.collected + 1, RUN_LIMITS.MAX_EXTRA_BALLS_GAME);
    this.sink.emit(EXTRA_BALL_EVENT);
  }

  /** A stage clear lights one extra ball. Cap: 1 for each stage, 2 for each game. */
  private lightExtraBall(stageNumber: number): void {
    const eb = this.state.extraBall;
    if (eb.lit || eb.litTotal >= RUN_LIMITS.MAX_EXTRA_BALLS_GAME) return;
    const litInStage = eb.lastLitStage === stageNumber ? 1 : 0;
    if (litInStage >= RUN_LIMITS.MAX_EXTRA_BALLS_STAGE) return;
    eb.lit = true;
    eb.litTotal += 1;
    eb.lastLitStage = stageNumber;
    this.sink.emit(EXTRA_BALL_LIT_EVENT);
  }

  // ───── stages and the draft ─────

  private isRunning(): boolean {
    const phase = this.state.phase;
    return phase === "play" || phase === "clear";
  }

  /** Start a stage. The twist of the stage is read here once and kept. */
  private beginStage(index: number): void {
    const s = this.state;
    this.stage = STAGES[index] as StageDef;
    this.twist = twistFor(this.stage);
    s.stageIndex = index;
    this.fight.reset();
    zeroSignals(s.signals);
    s.stagePoints[index] = 0;
    const d = s.draft;
    d.count = 0;
    d.elapsedMs = 0;
    d.autoPick = false;
    d.choices[0] = null;
    d.choices[1] = null;
    d.choices[2] = null;
    s.phase = "play";
    this.sink.emit({ kind: "stage_start", stage: this.stage.number, label: this.stage.label });
  }

  /** The goal is met: the boss of the stage comes. The boss fight runs until the boss is beaten. */
  private checkGoal(): void {
    const s = this.state;
    if (s.phase !== "play" || s.boss.active) return;
    if (!goalMet(s.signals, this.stage.goal)) return;
    this.fight.start(this.stage.number);
  }

  /** The boss is beaten: clear the stage, then open the draft or end the run. */
  private clearStage(): void {
    const s = this.state;
    s.cleared += 1;
    this.sink.emit({
      kind: "stage_clear",
      stage: this.stage.number,
      scoreDelta: s.signals.stagePoints,
    });
    this.lightExtraBall(this.stage.number);
    if (s.stageIndex >= STAGE_COUNT - 1) {
      s.phase = "clear";
      this.sink.emit(RUN_CLEAR_EVENT);
      return;
    }
    this.openDraft();
  }

  /** Draw the cards once. With no card to offer, the next stage starts at once. */
  private openDraft(): void {
    const s = this.state;
    const d = s.draft;
    d.count = drawDraft(this.rng, s.stacks, d.choices);
    if (d.count === 0) {
      this.beginStage(s.stageIndex + 1);
      return;
    }
    d.elapsedMs = 0;
    d.autoPick = s.featured;
    s.phase = "draft";
    const c = d.choices;
    this.sink.emit({ kind: "draft_open", choices: [c[0], c[1], c[2]] });
  }
}

/** Make a run director. */
export function createRunDirector(options: RunDirectorOptions): RunDirector {
  return new Director(options);
}

/** The run that does nothing. Every hook gives the base value. The rules use it with no run. */
export const NULL_RUN: RunRulesPort = {
  attachPayer(): void {},
  onGameStart(): void {},
  onBallEnd(): void {},
  tick(): void {},
  signal(): void {},
  onShot(): void {},
  scaleFor: () => 1,
  addStagePoints(): void {},
  launchSaveMs: (baseMs) => baseMs,
  comboWindowMs: (baseMs) => baseMs,
  modeMs: (baseMs) => baseMs,
  bonusMultStart: () => 1,
  shotLamp: () => 0,
  holdActive: () => false,
  onHoldTick(): void {},
  extraBallsEarned: () => 0,
  bossHit: () => false,
  takeBonusBalls: () => 0,
};
